import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { supabase } from "../../services/supabase";

export type Role = "admin" | "petugas" | null;

export type OfficerStatus = "Online" | "Jaga" | "Offline";

export interface User {
  id: string;
  name: string;
  username: string;
  jabatan: string;
  role: Role;
  avatar: string;
  lastSeen?: string | null;
  isOnline?: boolean;
}

export interface Visitor {
  id: string;
  nama: string;
  instansi: string;
  noHp: string;
  email: string;
  jenisKelamin: "Laki-laki" | "Perempuan";
  jenisLayanan: "Rekomendasi Statistik" | "Konsultasi Statistik" | "Layanan Perpustakaan";
  tujuan: string;
  mediaAkses: "Kunjungan Langsung" | "WhatsApp" | "Telepon" | "Email" | "Website" | "Surat Instansi/Eksternal" | "Instagram";
  timestamp: string;
  nomorAntrian: number;
  tipeKunjungan?: "antrian" | "mandiri";
  status: "Menunggu" | "Dilayani" | "Selesai";
  petugasId?: string;
  petugasNama?: string;
  rating?: number;
  ratingPelayanan?: {
    menyenangkan: number;
    unggul: number;
    luwes: number;
    ikhlas: number;
    adil: number;
    penampilan: number;
  };
  catatan?: string;
  laporTicket?: string;
  waStatus?: "Terkirim" | "Gagal" | null;
  suratUrl?: string;
  suratKeterangan?: string;
}

export interface Attendance {
  id: string;
  userId: string;
  userName: string;
  tanggal: string;
  masuk: string;
  pulang: string | null;
  status: "Hadir" | "Terlambat" | "Absen";
}

export interface Complaint {
  id: string;
  visitorNama: string;
  noHp: string;
  isi: string;
  timestamp: string;
  status: "Baru" | "Diproses" | "Selesai";
}

export interface LaporLog {
  id: string;
  visitorId: string;
  visitorNama: string;
  deskripsi: string;
  jenisKonsultasi: string;
  ticketId: string;
  timestamp: string;
  status: "Terkirim" | "Gagal";
}

export interface WaLog {
  id: string;
  officerId?: string;
  officerName?: string;
  phone: string;
  pesan: string;
  timestamp: string;
  status: "Terkirim" | "Gagal";
}

export interface WaInboxMessage {
  id: string;
  noHp: string;
  nama: string;
  pesan: string;
  timestamp: string;
  arah: "masuk" | "keluar";
  dibaca: boolean;
}

interface AppContextType {
  // Auth
  currentUser: User | null;
  authLoading: boolean;
  authSlow: boolean;
  authTimedOut: boolean;
  login: (username: string, password: string) => Promise<User | null>;
  logout: () => void;
  // Visitors
  visitors: Visitor[];
  visitorsLoading: boolean;
  addVisitor: (v: Omit<Visitor, "id" | "timestamp" | "nomorAntrian" | "status" | "tipeKunjungan">) => Promise<Visitor>;
  addVisitorMandiri: (v: Omit<Visitor, "id" | "timestamp" | "nomorAntrian" | "status" | "tipeKunjungan">) => Promise<Visitor>;
  updateVisitorStatus: (id: string, status: Visitor["status"], petugasId?: string, petugasNama?: string) => Promise<void>;
  updateVisitorRating: (id: string, ratingPelayanan: NonNullable<Visitor["ratingPelayanan"]>, catatan: string) => void;
  deleteVisitor: (id: string) => void;
  clearAllVisitors: () => void;
  updateVisitorSurat: (id: string, suratUrl: string, suratKeterangan?: string) => Promise<void>;
  updateVisitorData: (id: string, data: Pick<Visitor, "nama" | "instansi" | "noHp" | "email" | "jenisKelamin" | "jenisLayanan" | "tujuan" | "mediaAkses">) => Promise<void>;
  // Attendance
  attendance: Attendance[];
  attendanceLoading: boolean;
  checkIn: (userId: string) => Promise<{ error?: string }>;
  checkOut: (userId: string) => Promise<{ error?: string }>;
  // Complaints
  complaints: Complaint[];
  addComplaint: (c: Omit<Complaint, "id" | "timestamp" | "status">) => void;
  updateComplaintStatus: (id: string, status: Complaint["status"]) => void;
  // Lapor logs
  laporLogs: LaporLog[];
  addLaporLog: (log: Omit<LaporLog, "id" | "timestamp" | "ticketId" | "status"> & { ticketIdOverride?: string }) => LaporLog;
  // WA logs
  waLogs: WaLog[];
  addWaLog: (log: { phone: string; pesan: string }) => void;
  // WA inbox
  waInbox: WaInboxMessage[];
  addWaInboxMessage: (msg: Omit<WaInboxMessage, "id" | "dibaca" | "timestamp"> & { timestamp?: string; id?: string }) => void;
  markWaInboxRead: (noHp: string) => void;
  // Officers
  officers: User[];
  addOfficer: (o: Omit<User, "id" | "avatar">, password: string) => Promise<{ ok: boolean; error?: string }>;
  updateOfficer: (id: string, o: Omit<User, "id" | "avatar">) => void;
  deleteOfficer: (id: string) => void;
  resetOfficerPassword: (officerId: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  updateLastSeen: () => void;
  getOfficerStatus: (officer: User) => OfficerStatus;
}

// ── Helper: Supabase row → Visitor ──────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dbToVisitor = (row: any): Visitor => ({
  id: row.id,
  nama: row.nama,
  instansi: row.instansi ?? "",
  noHp: row.no_hp ?? "",
  email: row.email ?? "",
  jenisKelamin: row.jenis_kelamin,
  jenisLayanan: row.jenis_layanan,
  tujuan: row.tujuan ?? "",
  mediaAkses: row.media_akses ?? "Kunjungan Langsung",
  timestamp: row.created_at,
  nomorAntrian: row.nomor_antrian,
  status: row.status,
  petugasId: row.petugas_id ?? undefined,
  petugasNama: row.petugas_nama ?? undefined,
  rating: row.rating ?? undefined,
  ratingPelayanan: row.rating_pelayanan ?? undefined,
  catatan: row.catatan ?? undefined,
  laporTicket: row.lapor_ticket ?? undefined,
  waStatus: row.wa_status ?? null,
  suratUrl: row.surat_url ?? undefined,
  suratKeterangan: row.surat_keterangan ?? undefined,
  tipeKunjungan: (row.tipe_kunjungan as "antrian" | "mandiri") ?? "antrian",
});

// ── Helper: Supabase row → Complaint ────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dbToComplaint = (row: any): Complaint => ({
  id: row.id,
  visitorNama: row.visitor_nama,
  noHp: row.no_hp ?? "",
  isi: row.isi,
  timestamp: row.created_at,
  status: row.status,
});

// ── Helper: Supabase row → Attendance ───────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dbToAttendance = (row: any): Attendance => ({
  id: row.id,
  userId: row.user_id,
  userName: row.user_name,
  tanggal: row.tanggal,
  masuk: row.masuk,
  pulang: row.pulang ?? null,
  status: row.status,
});

// ── Helper: Supabase row → LaporLog ─────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dbToLaporLog = (row: any): LaporLog => ({
  id: row.id,
  visitorId: row.visitor_id ?? "",
  visitorNama: row.visitor_nama,
  deskripsi: row.deskripsi,
  jenisKonsultasi: row.jenis_konsultasi,
  ticketId: row.ticket_id,
  timestamp: row.created_at,
  status: row.status,
});

// ── Helper: Supabase row → WaLog ────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dbToWaLog = (row: any): WaLog => ({
  id: row.id,
  officerId: row.officer_id ?? undefined,
  officerName: row.officer_name ?? undefined,
  phone: row.phone,
  pesan: row.pesan,
  timestamp: row.created_at,
  status: row.status ?? "Terkirim",
});

// ── Helper: Supabase row → User (Officer) ───────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dbToOfficer = (row: any): User => ({
  id: row.id,
  name: row.name,
  username: row.username,
  jabatan: row.jabatan ?? "",
  role: row.role,
  avatar: row.avatar ?? row.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase(),
  lastSeen: row.last_seen ?? null,
  isOnline: row.is_online ?? false,
});

// ── Fallback mock officers (dipakai jika Supabase belum diset) ───────────────
const MOCK_OFFICERS: User[] = [
  { id: "u1", name: "Baiq Rini",        username: "baiqrini",       jabatan: "PJ PST",       role: "admin",   avatar: "BR" },
  { id: "u2", name: "Nora Dzulvawan",   username: "nora.dzulvawan",  jabatan: "Petugas Layanan",  role: "petugas", avatar: "ND" },
  { id: "u3", name: "M. Hawari Alfath", username: "hawari.alfath",   jabatan: "Petugas Layanan",  role: "petugas", avatar: "HA" },
  { id: "u4", name: "Nadya Namira",     username: "nadya.namira",    jabatan: "Petugas Layanan",  role: "petugas", avatar: "NN" },
];


const AppContext = createContext<AppContextType | null>(null);


const EMAIL_DOMAIN = "@bps.go.id";

export function AppProvider({ children }: { children: React.ReactNode }) {
  // ── Auth — Supabase Auth ──────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authSlow, setAuthSlow] = useState(false);
  const [authTimedOut, setAuthTimedOut] = useState(false);

  // ── Visitors — Supabase ───────────────────────────────────────────────────
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [visitorsLoading, setVisitorsLoading] = useState(true);

  // ── Officers — Supabase (hanya diload setelah authenticated) ─────────────
  const [officers, setOfficers] = useState<User[]>(MOCK_OFFICERS);

  // ── Complaints — Supabase ─────────────────────────────────────────────────
  const [complaints, setComplaints] = useState<Complaint[]>([]);

  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [laporLogs, setLaporLogs] = useState<LaporLog[]>([]);
  const [waLogs, setWaLogs] = useState<WaLog[]>([]);
  const [waInbox, setWaInbox] = useState<WaInboxMessage[]>([]);

  // ══════════════════════════════════════════════════════════════════════════
  // 0. ATTENDANCE + LAPOR_LOGS — fetch + polling 30 detik
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!currentUser) return;

    const fetchAttendance = () =>
      supabase.from("attendance").select("id,user_id,user_name,tanggal,masuk,pulang,status").order("tanggal", { ascending: false })
        .then(({ data }) => { if (data) setAttendance(data.map(dbToAttendance)); });

    setAttendanceLoading(true);
    fetchAttendance().then(() => setAttendanceLoading(false));

    supabase.from("lapor_logs").select("id,visitor_id,visitor_nama,deskripsi,jenis_konsultasi,ticket_id,created_at,status").order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setLaporLogs(data.map(dbToLaporLog)); });

    supabase.from("wa_logs").select("id,officer_id,officer_name,phone,pesan,created_at,status").order("created_at", { ascending: false }).limit(300)
      .then(({ data }) => { if (data) setWaLogs(data.map(dbToWaLog)); });

  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── OFFICERS + ATTENDANCE — combined polling 30 detik, anti race condition ──
  const isPolling = useRef(false);
  useEffect(() => {
    if (!currentUser) return;
    const t = setInterval(async () => {
      if (isPolling.current) return;
      isPolling.current = true;
      try {
        const [{ data: officersData }, { data: attData }] = await Promise.all([
          supabase.from("officers").select(OFFICER_COLS),
          supabase.from("attendance").select("id,user_id,user_name,tanggal,masuk,pulang,status").order("tanggal", { ascending: false }),
        ]);
        // React 18 auto-batches async state updates — no need for unstable_batchedUpdates
        if (officersData && officersData.length > 0) setOfficers(officersData.map(dbToOfficer));
        if (attData) setAttendance(attData.map(dbToAttendance));
      } finally {
        isPolling.current = false;
      }
    }, 30_000);
    return () => clearInterval(t);
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════════════════
  // 1. VISITORS — fetch + Realtime
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel>;

    const fetchVisitors = async () => {
      setVisitorsLoading(true);
      // Batasi ke tahun berjalan — data lama tetap ada di DB, bisa di-export via Laporan
      const currentYear = new Date().getFullYear();
      const { data, error } = await supabase
        .from("visitors")
        .select("id,nama,instansi,no_hp,email,jenis_kelamin,jenis_layanan,tujuan,media_akses,created_at,nomor_antrian,tipe_kunjungan,status,petugas_id,petugas_nama,rating,rating_pelayanan,catatan,lapor_ticket,wa_status,surat_url,surat_keterangan")
        .gte("created_at", `${currentYear}-01-01T00:00:00`)
        .order("nomor_antrian", { ascending: true });
      if (!error && data) setVisitors(data.map(dbToVisitor));
      setVisitorsLoading(false);
    };

    fetchVisitors();

    // Re-fetch saat tab aktif kembali (Realtime bisa putus saat tab/laptop tidur)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchVisitors();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Realtime — perubahan dari petugas mana pun langsung masuk
    try {
      channel = supabase
        .channel("visitors-realtime")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "visitors" }, ({ new: row }) => {
          // Idempoten: jika sudah ditambahkan secara optimistik oleh addVisitor(), skip
          setVisitors(prev => prev.some(v => v.id === row.id) ? prev : [...prev, dbToVisitor(row)]);
        })
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "visitors" }, ({ new: row }) => {
          setVisitors(prev => prev.map(v => v.id === row.id ? dbToVisitor(row) : v));
        })
        .on("postgres_changes", { event: "DELETE", schema: "public", table: "visitors" }, ({ old: row }) => {
          setVisitors(prev => prev.filter(v => v.id !== row.id));
        })
        .subscribe((status, err) => {
          if (err) console.warn("[Realtime] visitors subscribe error:", status, err.message);
        });
    } catch (err) {
      // WebSocket tidak tersedia di browser ini — Realtime tidak aktif, fallback ke visibilitychange re-fetch
      console.warn("[Realtime] WebSocket unavailable, Realtime dinonaktifkan:", err);
    }

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // 2. COMPLAINTS — fetch
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchComplaints = async () => {
      const { data, error } = await supabase
        .from("complaints")
        .select("id,visitor_nama,no_hp,isi,created_at,status")
        .order("created_at", { ascending: false });
      if (!error && data) setComplaints(data.map(dbToComplaint));
    };
    fetchComplaints();
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // 3. AUTH — Supabase Auth session + officers fetch setelah login
  // ══════════════════════════════════════════════════════════════════════════
  const OFFICER_COLS = "id, name, username, jabatan, role, avatar, last_seen, is_online, auth_id";

  const loadUserProfile = async (authId: string) => {
    // Jalankan kedua query parallel — kolom spesifik agar payload lebih kecil
    const [{ data }, { data: allOfficers }] = await Promise.all([
      supabase.from("officers").select(OFFICER_COLS).eq("auth_id", authId).single(),
      supabase.from("officers").select(OFFICER_COLS),
    ]);
    if (data) {
      const user = dbToOfficer(data);
      setCurrentUser(user);
      if (allOfficers && allOfficers.length > 0) setOfficers(allOfficers.map(dbToOfficer));
      return user;
    }
    return null;
  };

  useEffect(() => {
    // Tahap 1 — 8 detik: beri tahu user koneksi lambat (cold start free tier wajar sampai 15-20 detik)
    const slowTimeout = setTimeout(() => setAuthSlow(true), 8_000);

    // Tahap 2 — paksa selesai agar login form tetap tampil (cold start free tier bisa 15-20 detik)
    const hardTimeout = setTimeout(() => {
      console.warn("[Auth] Safety timeout — paksa authLoading=false. Cek koneksi/token Supabase.");
      setAuthLoading(false);
      setAuthTimedOut(true);
    }, 30_000);

    // onAuthStateChange menangani: INITIAL_SESSION, SIGNED_IN, SIGNED_OUT
    // TOKEN_REFRESHED di-skip — token diperbaharui otomatis di background,
    // tidak perlu reload profil (menghindari interference dengan flow login)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;
      clearTimeout(slowTimeout);
      clearTimeout(hardTimeout);
      setAuthSlow(false);
      setAuthTimedOut(false);
      // Safety net: kalau loadUserProfile hang (Supabase cold start), paksa selesai
      const profileTimeout = setTimeout(() => {
        console.warn("[Auth] loadUserProfile timeout — paksa authLoading=false");
        setAuthLoading(false);
      }, 20_000);
      try {
        if (session?.user) {
          await loadUserProfile(session.user.id);
        } else {
          setCurrentUser(null);
        }
      } catch {
        setCurrentUser(null);
      } finally {
        clearTimeout(profileTimeout);
        setAuthLoading(false);
      }
    });

    return () => {
      clearTimeout(slowTimeout);
      clearTimeout(hardTimeout);
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const login = async (username: string, password: string): Promise<User | null> => {
    // Bersihkan sesi lokal dulu — cegah TOKEN_REFRESHED akun lama menimpa sesi baru.
    // scope:'local' = hapus dari localStorage saja, tanpa network call (tidak lambat).
    await supabase.auth.signOut({ scope: 'local' });

    const email = `${username}${EMAIL_DOMAIN}`;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error("[Login] Auth error:", error.message);
      return null;
    }
    if (!data.user) {
      console.error("[Login] No user returned");
      return null;
    }
    const profile = await loadUserProfile(data.user.id);
    if (!profile) console.error("[Login] Officer profile not found for auth_id:", data.user.id);
    return profile;
  };

  const logout = () => {
    // Set offline sebelum sign out
    if (currentUser) {
      supabase.from("officers").update({ is_online: false }).eq("id", currentUser.id).then(() => {});
    }
    // scope:'local' — hapus sesi lokal langsung (sync), tidak ada network call.
    // Tanpa ini, SIGNED_OUT bisa nyasar setelah login baru berhasil → loop redirect.
    supabase.auth.signOut({ scope: 'local' });
    setCurrentUser(null);
    setWaLogs([]);
    sessionStorage.removeItem("pst_login_attempts");
  };

  // ── UPDATE LAST SEEN ───────────────────────────────────────────────────────
  const updateLastSeen = () => {
    if (!currentUser) return;
    const now = new Date().toISOString();
    supabase.from("officers")
      .update({ last_seen: now, is_online: true })
      .eq("id", currentUser.id)
      .then(() => {});
    setOfficers(prev => prev.map(o =>
      o.id === currentUser.id ? { ...o, lastSeen: now, isOnline: true } : o
    ));
  };

  // Panggil updateLastSeen setiap 5 menit saat login
  useEffect(() => {
    if (!currentUser) return;
    updateLastSeen();
    const interval = setInterval(updateLastSeen, 5 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // ── GET OFFICER STATUS ─────────────────────────────────────────────────────
  const getOfficerStatus = (officer: User): OfficerStatus => {
    const today = new Date().toISOString().split("T")[0];
    const FIVE_MIN = 5 * 60 * 1000;
    if (officer.lastSeen && Date.now() - new Date(officer.lastSeen).getTime() < FIVE_MIN) {
      return "Online";
    }
    const todayAtt = attendance.find(a => a.userId === officer.id && a.tanggal === today);
    if (todayAtt && todayAtt.masuk && !todayAtt.pulang) return "Jaga";
    return "Offline";
  };

  // ── VISITORS CRUD ─────────────────────────────────────────────────────────
  const addVisitor = async (v: Omit<Visitor, "id" | "timestamp" | "nomorAntrian" | "status">): Promise<Visitor> => {
    const today = new Date().toISOString().split("T")[0];

    // Dupe check dan generate nomor antrian server-side secara paralel
    const [
      { data: dupeCheck, error: dupeErr },
      { data: nomorRaw,  error: rpcErr  },
    ] = await Promise.all([
      supabase
        .from("visitors")
        .select("nomor_antrian")
        .eq("no_hp", v.noHp)
        .in("status", ["Menunggu", "Dilayani"])
        .gte("created_at", `${today}T00:00:00`)
        .maybeSingle(),
      supabase.rpc("generate_nomor_antrian"),
    ]);

    if (dupeErr)  console.error("[addVisitor] dupe check error:", dupeErr.message, dupeErr.code);
    if (rpcErr)   console.error("[addVisitor] RPC error:", rpcErr.message, rpcErr.code);

    if (dupeCheck) {
      const err = new Error("Nomor HP sudah memiliki antrian aktif") as any;
      err.code = "DUPE_PHONE";
      err.nomorAntrian = dupeCheck.nomor_antrian;
      throw err;
    }

    // RPC mengembalikan TEXT "001", "002", dst — parse ke INT untuk kolom nomor_antrian
    const nextAntrian = parseInt(nomorRaw as string, 10) || 1;

    const { data, error } = await supabase
      .from("visitors")
      .insert({
        nama:           v.nama,
        instansi:       v.instansi,
        no_hp:          v.noHp,
        email:          v.email,
        jenis_kelamin:  v.jenisKelamin,
        jenis_layanan:  v.jenisLayanan,
        tujuan:         v.tujuan,
        media_akses:    v.mediaAkses,
        surat_url:      v.suratUrl ?? null,
        surat_keterangan: v.suratKeterangan ?? null,
        nomor_antrian:  nextAntrian,
        status:         "Menunggu",
      })
      .select()
      .single();

    if (error || !data) {
      console.error("[addVisitor] INSERT error:", error?.message, error?.code);
      throw new Error(error?.message ?? "Gagal menyimpan data ke database");
    }

    const visitor = dbToVisitor(data);

    // Tambah ke state lokal langsung — tidak menunggu Realtime karena Supabase
    // kadang tidak mengirim event INSERT kembali ke client yang melakukan insert
    setVisitors(prev => prev.some(v => v.id === visitor.id) ? prev : [...prev, visitor]);


    return visitor;
  };

  const addVisitorMandiri = async (v: Omit<Visitor, "id" | "timestamp" | "nomorAntrian" | "status" | "tipeKunjungan">): Promise<Visitor> => {
    const today = new Date().toISOString().split("T")[0];

    const { data: dupeCheck, error: dupeErr } = await supabase
      .from("visitors")
      .select("id")
      .eq("no_hp", v.noHp)
      .in("status", ["Menunggu", "Dilayani"])
      .gte("created_at", `${today}T00:00:00`)
      .maybeSingle();

    if (dupeErr) console.error("[addVisitorMandiri] dupe check error:", dupeErr.message);

    if (dupeCheck) {
      const err = new Error("Nomor HP sudah terdaftar aktif hari ini") as any;
      err.code = "DUPE_PHONE_MANDIRI";
      throw err;
    }

    const { data, error } = await supabase
      .from("visitors")
      .insert({
        nama:             v.nama,
        instansi:         v.instansi,
        no_hp:            v.noHp,
        email:            v.email,
        jenis_kelamin:    v.jenisKelamin,
        jenis_layanan:    v.jenisLayanan,
        tujuan:           v.tujuan,
        media_akses:      v.mediaAkses,
        surat_url:        v.suratUrl ?? null,
        surat_keterangan: v.suratKeterangan ?? null,
        nomor_antrian:    0,
        tipe_kunjungan:   "mandiri",
        status:           "Selesai",
      })
      .select()
      .single();

    if (error || !data) {
      console.error("[addVisitorMandiri] INSERT error:", error?.message, error?.code);
      throw new Error(error?.message ?? "Gagal menyimpan data ke database");
    }

    const visitor = dbToVisitor(data);
    setVisitors(prev => prev.some(x => x.id === visitor.id) ? prev : [...prev, visitor]);
    return visitor;
  };

  const updateVisitorStatus = async (id: string, status: Visitor["status"], petugasId?: string, petugasNama?: string) => {
    // Hanya update petugas_id/petugas_nama jika eksplisit dikirim (dari petugas panel)
    // Jika tidak dikirim (dari admin panel), jangan overwrite data petugas yang sudah ada
    const patch: Record<string, unknown> = { status };
    if (petugasId !== undefined) patch.petugas_id = petugasId;
    if (petugasNama !== undefined) patch.petugas_nama = petugasNama;
    const { error } = await supabase.from("visitors").update(patch).eq("id", id);
    if (error) {
      console.error("[updateVisitorStatus]", error.message, error.code);
      // Sesi expired (JWT invalid / refresh token rotated oleh window lain) → paksa logout
      // ProtectedRoute akan redirect ke /login secara instan
      if (error.code === "PGRST301" || error.message?.includes("JWT") || error.message?.includes("refresh_token")) {
        supabase.auth.signOut({ scope: "local" });
        setCurrentUser(null);
      }
      return;
    }
    // Realtime UPDATE akan update state otomatis
    // Fallback lokal jika Realtime lambat:
    setVisitors(prev => prev.map(v => {
      if (v.id !== id) return v;
      return { ...v, status, ...(petugasId !== undefined ? { petugasId, petugasNama } : {}) };
    }));
  };

  const updateVisitorData = async (id: string, data: Pick<Visitor, "nama" | "instansi" | "noHp" | "email" | "jenisKelamin" | "jenisLayanan" | "tujuan" | "mediaAkses">) => {
    await supabase.from("visitors").update({
      nama: data.nama,
      instansi: data.instansi,
      no_hp: data.noHp,
      email: data.email,
      jenis_kelamin: data.jenisKelamin,
      jenis_layanan: data.jenisLayanan,
      tujuan: data.tujuan,
      media_akses: data.mediaAkses,
    }).eq("id", id);
    setVisitors(prev => prev.map(v => v.id === id ? { ...v, ...data } : v));
  };

  const updateVisitorSurat = async (id: string, suratUrl: string, suratKeterangan?: string) => {
    await supabase
      .from("visitors")
      .update({ surat_url: suratUrl, surat_keterangan: suratKeterangan ?? null })
      .eq("id", id);
    setVisitors(prev => prev.map(v =>
      v.id === id ? { ...v, suratUrl, suratKeterangan: suratKeterangan ?? v.suratKeterangan } : v
    ));
  };

  const updateVisitorRating = async (id: string, ratingPelayanan: NonNullable<Visitor["ratingPelayanan"]>, catatan: string) => {
    const values = Object.values(ratingPelayanan);
    const rating = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    await supabase
      .from("visitors")
      .update({ rating, rating_pelayanan: ratingPelayanan, catatan })
      .eq("id", id);
    setVisitors(prev => prev.map(v => v.id === id ? { ...v, ratingPelayanan, rating, catatan } : v));
  };

  const deleteVisitor = async (id: string) => {
    if (currentUser?.role !== "admin") return;
    await supabase.from("visitors").delete().eq("id", id);
    setVisitors(prev => prev.filter(v => v.id !== id));
  };

  const clearAllVisitors = async () => {
    if (currentUser?.role !== "admin") return;
    await supabase.from("visitors").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    setVisitors([]);
  };

  // ── COMPLAINTS CRUD ───────────────────────────────────────────────────────
  const addComplaint = async (c: Omit<Complaint, "id" | "timestamp" | "status">) => {
    const { data, error } = await supabase
      .from("complaints")
      .insert({ visitor_nama: c.visitorNama, no_hp: c.noHp, isi: c.isi })
      .select()
      .single();
    if (!error && data) {
      setComplaints(prev => [dbToComplaint(data), ...prev]);
    } else {
      // Fallback
      setComplaints(prev => [{
        ...c,
        id: `c${Date.now()}`,
        timestamp: new Date().toISOString(),
        status: "Baru",
      }, ...prev]);
    }
  };

  const updateComplaintStatus = async (id: string, status: Complaint["status"]) => {
    await supabase.from("complaints").update({ status }).eq("id", id);
    setComplaints(prev => prev.map(c => c.id === id ? { ...c, status } : c));
  };

  // ── OFFICERS CRUD ─────────────────────────────────────────────────────────
  const addOfficer = async (o: Omit<User, "id" | "avatar">, password: string): Promise<{ ok: boolean; error?: string }> => {
    const avatar = o.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase();
    const { data, error } = await supabase
      .from("officers")
      .insert({ ...o, avatar, password: "managed_by_supabase_auth" })
      .select()
      .single();

    if (error || !data) {
      return { ok: false, error: error?.message ?? "Gagal menyimpan petugas" };
    }

    setOfficers(prev => [...prev, dbToOfficer(data)]);

    // Buat Supabase Auth user di background — tidak blokir form close
    // (cold start Vercel bisa 2-5 detik; akun DB sudah tersimpan, login bisa dilakukan setelah proses ini selesai)
    fetch("/api/officer-create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: o.username, password, officerId: data.id }),
    }).catch(() => {});

    return { ok: true };
  };

  const updateOfficer = async (id: string, o: Omit<User, "id" | "avatar">) => {
    const avatar = o.name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase();
    await supabase.from("officers").update({ ...o, avatar }).eq("id", id);
    setOfficers(prev => prev.map(u => u.id === id ? { ...u, ...o, avatar } : u));
  };

  const deleteOfficer = async (id: string) => {
    await supabase.from("officers").delete().eq("id", id);
    setOfficers(prev => prev.filter(o => o.id !== id));
  };

  // ── ATTENDANCE (Supabase) ─────────────────────────────────────────────────
  const checkIn = async (userId: string): Promise<{ error?: string }> => {
    const today = new Date().toISOString().split("T")[0];
    // Guard ganda: cek Supabase langsung agar tidak menimpa masuk pagi
    // meskipun state lokal belum ter-load (misalnya setelah re-login)
    const { data: existing } = await supabase
      .from("attendance")
      .select("id")
      .eq("user_id", userId)
      .eq("tanggal", today)
      .maybeSingle();
    if (existing) return {}; // Sudah absen masuk hari ini — jangan timpa

    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const officer = officers.find(o => o.id === userId);
    const record: Attendance = {
      id: `att-${today}-${userId}`,
      userId,
      userName: officer?.name || "",
      tanggal: today,
      masuk: timeStr,
      pulang: null,
      // Shift Pagi: masuk sebelum 12.00, terlambat jika > 08.30
      // Shift Siang: masuk jam 12.00+, tidak ada konsep terlambat
      status: now.getHours() < 12
        ? (now.getHours() > 8 || (now.getHours() === 8 && now.getMinutes() > 30) ? "Terlambat" : "Hadir")
        : "Hadir",
    };

    const { error } = await supabase.from("attendance").insert({
      id: record.id, user_id: userId, user_name: record.userName,
      tanggal: today, masuk: timeStr, status: record.status,
    });

    if (error) return { error: "Gagal menyimpan presensi. Coba lagi." };
    setAttendance(prev => [...prev, record]);
    return {};
  };

  const checkOut = async (userId: string): Promise<{ error?: string }> => {
    const today = new Date().toISOString().split("T")[0];
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

    const { error } = await supabase.from("attendance").update({ pulang: timeStr })
      .eq("id", `att-${today}-${userId}`);

    if (error) return { error: "Gagal menyimpan jam pulang. Coba lagi." };
    setAttendance(prev => prev.map(a =>
      a.userId === userId && a.tanggal === today ? { ...a, pulang: timeStr } : a
    ));
    return {};
  };

  // ── LAPOR LOGS (Supabase) ─────────────────────────────────────────────────
  const addLaporLog = (log: Omit<LaporLog, "id" | "timestamp" | "ticketId" | "status"> & { ticketIdOverride?: string }): LaporLog => {
    const { ticketIdOverride, ...rest } = log;
    const id = `l${Date.now()}`;
    const ticketId = ticketIdOverride ?? `LPR-2026-${String(laporLogs.length + 1).padStart(4, "0")}`;
    const newLog: LaporLog = {
      ...rest, id, timestamp: new Date().toISOString(), ticketId, status: "Terkirim",
    };
    setLaporLogs(prev => [newLog, ...prev]);
    // Persist ke Supabase (fire & forget)
    supabase.from("lapor_logs").insert({
      id, visitor_id: rest.visitorId, visitor_nama: rest.visitorNama,
      deskripsi: rest.deskripsi, jenis_konsultasi: rest.jenisKonsultasi,
      ticket_id: ticketId, status: "Terkirim",
    }).then(() => {});
    return newLog;
  };

  // ── RESET PASSWORD PETUGAS (admin only via server-side API) ──────────────
  const resetOfficerPassword = async (officerId: string, newPassword: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await fetch("/api/officer-reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ officerId, newPassword }),
      });
      return await res.json();
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : "Gagal" };
    }
  };

  // ── WA LOGS (Supabase) ────────────────────────────────────────────────────
  const addWaLog = (log: { phone: string; pesan: string }) => {
    const optimistic: WaLog = {
      id: `wa${Date.now()}`,
      officerId: currentUser?.id,
      officerName: currentUser?.name,
      phone: log.phone,
      pesan: log.pesan,
      timestamp: new Date().toISOString(),
      status: "Terkirim",
    };
    setWaLogs(prev => [optimistic, ...prev]);
    supabase.from("wa_logs").insert({
      officer_id:   currentUser?.id ?? null,
      officer_name: currentUser?.name ?? null,
      phone:        log.phone,
      pesan:        log.pesan,
      status:       "Terkirim",
    }).then(() => {});
  };

  const addWaInboxMessage = (msg: Omit<WaInboxMessage, "id" | "dibaca" | "timestamp"> & { timestamp?: string; id?: string }) => {
    setWaInbox(prev => [...prev, {
      ...msg,
      id: msg.id ?? `wai${Date.now()}`,
      timestamp: msg.timestamp ?? new Date().toISOString(),
      dibaca: msg.arah === "keluar",
    }]);
  };

  const markWaInboxRead = (noHp: string) => {
    setWaInbox(prev => prev.map(m => m.noHp === noHp ? { ...m, dibaca: true } : m));
  };

  return (
    <AppContext.Provider value={{
      currentUser, authLoading, authSlow, authTimedOut, login, logout,
      visitors, visitorsLoading, addVisitor, addVisitorMandiri, updateVisitorStatus, updateVisitorRating,
      deleteVisitor, clearAllVisitors, updateVisitorSurat, updateVisitorData,
      attendance, attendanceLoading, checkIn, checkOut,
      complaints, addComplaint, updateComplaintStatus,
      laporLogs, addLaporLog,
      waLogs, addWaLog,
      waInbox, addWaInboxMessage, markWaInboxRead,
      officers, addOfficer, updateOfficer, deleteOfficer, resetOfficerPassword,
      updateLastSeen, getOfficerStatus,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
